import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from 'uuid';
import { useDispatch, useSelector } from "react-redux";
import { setDialogHandle, setIsDialogOpen } from "../../actions/dialog";
import { HTTPMethods } from "../../yoello-lib/modules/API/API";
import APIExtensions from "../../yoello-lib/modules/API2/APIDefs";
import { useAPIV2 } from "../../yoello-lib/modules/API2/useAPIv2";

import NewAreaDialog from "./ui/NewAreaDialog";
import { useStyles } from "./ui/styles";
import { IOrderLocationsGroup, IOrderLocation } from "./state/venueAreas.reducer";
import { IReduxAppState } from "../../reducers";
import { deleteVenueArea, setVenueAreas, updateVenueArea } from "./state";
import { useSnackbarMessages } from "../../yoello-lib/modules/components/snackbar/SnackbarContext";
import { createOrderLocationGroupsObject } from "./utils/createOrderLocationGroups";
import { IVenueGroup } from "../groupsManagement/state/groupsManagement.reducer";
import { IDineInFormState } from "./DineInContainer";
import IVenueSettings from "../../interfaces/IVenueSettings";





export function useVenueAreas(dispatch: any, venue_group_id?:string, dineInFromState?:IDineInFormState , settingsId?:string) {
    const dispatcher = useDispatch();
    const [isLoading , setIsLoading] = useState<boolean>(false);
    const venueId = useSelector((state: IReduxAppState) => state.selectedVenueId);
    const [error, setError] = useState<Record<string, string[]>>({});
    const { t } = useTranslation();
    const classes = useStyles();
    const { callAPI:updateDineInSettings } = useAPIV2<IVenueSettings>(APIExtensions.venueSettings, {
        yoelloAPIParams: { venue_id: venueId },
        selectorKeyParams: { venue_id: venueId }
    });
    const { callAPI, apiData } = useAPIV2<IOrderLocationsGroup[]>(APIExtensions.orderLocations, { onMountDisabled: true });
    const { callAPI: callGroups, apiData:groupsAPIData } = useAPIV2<IOrderLocationsGroup[]>(APIExtensions.venueGroups, {
        canAPIGetCacheValue: true,
        onMountDisabled: true,
      });
    const { callAPI: callGroupSettigs } = useAPIV2<IVenueGroup>(APIExtensions.venueGroups, {
        onMountDisabled: true,
    });  
    const { sendSnackbarMessage } = useSnackbarMessages()!;

    useEffect(() => {
        setIsLoading(true);
        if(venue_group_id){
            callGroups(HTTPMethods.GET, {
                query: `${venue_group_id}/order_location_group`,
            })
        }else{
            callAPI(HTTPMethods.GET, {
                query: `order_location_groups`,
                yoelloAPIParams: { venue_id: venueId }
            })
        }
       
        // eslint-disable-next-line    
    }, [venue_group_id]);

    useEffect(()=>{
      if(apiData){
        generateVenueAreas(apiData);
        setIsLoading(false);
      }
    // eslint-disable-next-line  
    },[apiData])

    useEffect(()=>{
      if(groupsAPIData){
        generateVenueAreas(groupsAPIData);
        setIsLoading(false);
      }
    // eslint-disable-next-line  
    },[venue_group_id, groupsAPIData])

    function generateVenueAreas(orderLocationGroups:IOrderLocationsGroup[]){
        if (orderLocationGroups?.length > 0) {
            // eslint-disable-next-line
            dispatch(setVenueAreas(orderLocationGroups.reduce((groups: Record<string, IOrderLocationsGroup>, group: IOrderLocationsGroup) => (groups[group.id] = group, groups), {})))
        }
    }
    //delete venue area 
    function onDeleteVenueArea(id: string , groups:IOrderLocationsGroup[]) {
        if(groups.length === 1 && dineInFromState?.table_enabled && !venue_group_id){
            sendSnackbarMessage(t("errors.venueAreaRanges.deleteArea"), "error");
        }else{
            dispatch(deleteVenueArea(id));
        }
    }

    //update dine in settings 
    async function updateTableServiceSetting(){
        if(!isLoading){
            setIsLoading(true);
        }
        const settings = await updateDineInSettings(HTTPMethods.PUT, {
            body: {
                ...dineInFromState
            }, query: settingsId,
        });
        if(settings.isSuccessful){
            sendSnackbarMessage(t("snackbar.updateSuccess"), "success");
            setIsLoading(false);
        }  
    }
    //update venue area ranges 
    async function updateVenueAreaRanges(orderLocationGroups: Record<string, IOrderLocationsGroup>) {
        const { groups, error, enabledCount }: { groups: any, error: any, enabledCount:number } = createOrderLocationGroupsObject(Object.values(orderLocationGroups) , venue_group_id);
        if(enabledCount === 0) {
            let isMultiOrderingEnabled = false;
            if(venue_group_id){
                const { data, isSuccessful} = await callGroupSettigs(HTTPMethods.GET,{
                    query:venue_group_id
                });
                if(isSuccessful && data){
                    isMultiOrderingEnabled = data.group_settings.multi_ordering_enabled
                }
            }
            if(dineInFromState?.table_enabled && !venue_group_id){
                sendSnackbarMessage(t("errors.venueAreaRanges.tableEnabled"), "error");
                return;
            }
            if(isMultiOrderingEnabled && venue_group_id){
                sendSnackbarMessage(t("errors.venueAreaRanges.areaEnabled"), "error");
                return;
            }
           
        }
        if (Object.keys(error).length > 0) {
            setError(error);
            const hasEmptyLocation = Object.values(error).find((error:any) => error.length === 0);
            if(hasEmptyLocation){
                sendSnackbarMessage(t("errors.venueAreaRanges.error"), "error");
            }
        } else {
            setError({});
            try {
                let result;
                setIsLoading(true);
                if(venue_group_id){
                    result = await callGroups(HTTPMethods.POST, {
                        query: `${venue_group_id}/order_location_group`,
                        body: groups
                    });
                    if(result.isSuccessful){
                        sendSnackbarMessage(t("snackbar.updateSuccess"), "success");
                        setIsLoading(false);
                    }
                }else{
                    result = await callAPI(HTTPMethods.POST, {
                        query: 'order_location_groups',
                        body: groups,
                        yoelloAPIParams: { venue_id: venueId }
                    });
                    if (result.isSuccessful && dineInFromState && settingsId) {
                        await updateTableServiceSetting();
                    }
                }
            } catch (error) {
                sendSnackbarMessage(error as string, 'error');
                setIsLoading(false);
            }
        }

    }

    function handleVenueArea(group?: IOrderLocationsGroup) {
        dispatcher(
            setDialogHandle({
                Component: NewAreaDialog,
                ComponentProps: {
                    group,
                    venueId,
                    venue_group_id,
                    dispatch
                },
                onClose: () => dispatcher(setIsDialogOpen(false)),
                onCloseIcon: () => dispatcher(setIsDialogOpen(false)),
                isFormDialog: true,
                dialogTitle: group?.id ? t("forms.settings.editNewVenueArea") : t("forms.settings.createNewVenueArea"),
                styles: { className: classes.modalContainer }
            })
        );
    }

    function onOrderLocationCreate(group: IOrderLocationsGroup) {
        const location_id = uuid();
        group.order_locations.push({
            id: location_id,
            group_id: group.id,
            ...( group.venue_group_id ? {venue_group_id:group.venue_group_id} : {venue_id: group.venue_id }),
            isNewField: true
        })
        dispatch(updateVenueArea(group))

    }
    function onOrderLocationChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index: string, group: IOrderLocationsGroup) {
        const { name, value } = e.target;
        const locationIndex = group.order_locations.findIndex((obj => obj.id === index));
        if (locationIndex > -1) {
            group.order_locations[locationIndex] = {
                ...group.order_locations[locationIndex],
                group_id: group.id,
                ...( group.venue_group_id ? {venue_group_id:group.venue_group_id} : {venue_id: group.venue_id }),
                [name]: value
            } as IOrderLocation
            if (name === "name_public") {
                if (value.length > 0) {
                    if (error[group.id as string]) {
                        if (error[group.id as string].length === 0) {
                            delete error[group.id as string]
                        } else {
                            const locationIndex = error[group.id as string].findIndex((id => id === index));
                            if (locationIndex > -1) {
                                error[group.id as string].splice(locationIndex, 1);
                                if (error[group.id as string].length === 0) {
                                    delete error[group.id as string]
                                }
                            }
                        }
                    }
                    setError(error)
                } else {
                    if (!error[group.id!]) {
                        error[group.id!] = [];
                    }
                    error[group.id!].push(index);
                    setError(error)
                }
            }
            dispatch(updateVenueArea(group))
        }
    }
    function onOrderLocationDelete(index: string, group: IOrderLocationsGroup) {
        
        const locationIndex = group.order_locations.findIndex((obj => obj.id === index));
        if (locationIndex > -1) {
            group.order_locations.splice(locationIndex, 1);
            dispatch(updateVenueArea(group))
        }
    }
    function onStatusChangeHandler(value:boolean,  group: IOrderLocationsGroup) {
            dispatch(updateVenueArea({
                ...group,
                enabled: value
            }))
    }


    return {
        handleVenueArea,
        onDeleteVenueArea,
        updateVenueAreaRanges,
        onOrderLocationChange,
        onOrderLocationDelete,
        onOrderLocationCreate,
        onStatusChangeHandler,
        updateTableServiceSetting,
        isLoading,
        error
    }
}